/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { CheckIcon, EyeIcon, EyeOffIcon, XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { forwardRef, useId, useState } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'

import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '../../ui/input-group'

export const MIN_PASSWORD_LENGTH = 6
export const MAX_PASSWORD_LENGTH = 128

export const PASSWORD_LETTER_REGEX = /[a-zA-Z]/
export const PASSWORD_NUMBER_REGEX = /[\d]/

export type PasswordFormFieldProps = {
  autoComplete?: ComponentProps<typeof InputGroupInput>['autoComplete']
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  inputGroupProps?: ComponentProps<typeof InputGroup>
  inputProps?: Omit<ComponentProps<typeof InputGroupInput>, 'type'>
  label?: string
  maxPasswordLength?: number
  minPasswordLength?: number
  name: string
  passwordLetterRegex?: RegExp
  passwordNumberRegex?: RegExp
  placeholder?: string
  required?: boolean
  showValidator?: boolean
  validation?: RegisterOptions
}

export const PasswordFormField = forwardRef<HTMLInputElement, PasswordFormFieldProps>((props, forwardedRef) => {
  const { register, control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const { ref, onBlur: registerOnBlur, ...workRegister } = register(props.name, props.validation)

  const passwordValue = useWatch({ control, name: props.name })
  const [visible, setVisible] = useState(false)
  const [hasBeenFocused, setHasBeenFocused] = useState(false)
  const inputId = useId()

  const { inputProps } = props
  const { ref: inputRef, className: inputClassName, onFocus, onBlur, ...restInputProps } = inputProps ?? {}

  const error = errors[props.name]
  const invalid = !!error
  const passwordText = typeof passwordValue === 'string' ? passwordValue : ''
  const showRequirements = props.showValidator === true && hasBeenFocused && passwordText.length > 0

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <InputGroup {...props.inputGroupProps} className={cn('flex-1', props.inputGroupProps?.className)}>
        <InputGroupInput
          suppressHydrationWarning
          {...workRegister}
          {...restInputProps}
          aria-invalid={invalid}
          autoComplete={props.autoComplete}
          className={cn(inputClassName)}
          disabled={props.disabled}
          id={inputId}
          onBlur={(event) => {
            registerOnBlur(event)
            onBlur?.(event)
          }}
          onFocus={(event) => {
            setHasBeenFocused(true)
            onFocus?.(event)
          }}
          placeholder={props.placeholder}
          ref={mergeRefs(ref, forwardedRef, inputRef)}
          required={props.required}
          type={visible ? 'text' : 'password'}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            aria-label={visible ? 'Hide password' : 'Show password'}
            disabled={props.disabled}
            onClick={() => setVisible((v) => !v)}
            type="button"
          >
            {visible ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      {showRequirements ? (
        <PasswordVisualValidation
          maxPasswordLength={props.maxPasswordLength ?? MAX_PASSWORD_LENGTH}
          minPasswordLength={props.minPasswordLength ?? MIN_PASSWORD_LENGTH}
          password={passwordText}
          passwordLetterRegex={props.passwordLetterRegex ?? PASSWORD_LETTER_REGEX}
          passwordNumberRegex={props.passwordNumberRegex ?? PASSWORD_NUMBER_REGEX}
        />
      ) : null}
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

PasswordFormField.displayName = 'PasswordFormField'

function PasswordVisualValidation({
  password,
  passwordLetterRegex,
  passwordNumberRegex,
  minPasswordLength,
  maxPasswordLength,
}: {
  password: string
  passwordLetterRegex: RegExp
  passwordNumberRegex: RegExp
  minPasswordLength: number
  maxPasswordLength: number
}) {
  const letterValid = () => password?.match(passwordLetterRegex)
  const numberValid = () => password?.match(passwordNumberRegex)
  const minValid = () => password?.length >= minPasswordLength
  const maxValid = () => password?.length < maxPasswordLength

  return (
    <div className="grid w-full grid-cols-2 gap-x-4 gap-y-1 text-sm">
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1">
          {letterValid() ? <CheckIcon className="size-5 text-green-600" /> : <XIcon className="size-5 text-red-500" />}
          Have 1 letter
        </span>
        <span className="flex items-center gap-1">
          {numberValid() ? <CheckIcon className="size-5 text-green-600" /> : <XIcon className="size-5 text-red-500" />}
          Have 1 number
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1">
          {minValid() ? <CheckIcon className="size-5 text-green-600" /> : <XIcon className="size-5 text-red-500" />}
          Min {minPasswordLength} characters
        </span>
        <span className="flex items-center gap-1">
          {maxValid() ? <CheckIcon className="size-5 text-green-600" /> : <XIcon className="size-5 text-red-500" />}
          Max {maxPasswordLength} characters
        </span>
      </div>
    </div>
  )
}
